import { useCallback, useRef, useState, useEffect } from 'react';

import { Room } from 'twilio-video';

import type { ErrorCallback } from '../types';

interface UseWhiteboardRecordingOptions {
    room: Room | null;
    onError: ErrorCallback;
    frameRate?: number;
    recordingEnabled?: boolean;
}

export default function useWhiteboardRecording({
    room,
    onError,
    frameRate = 30,
    recordingEnabled = false
}: UseWhiteboardRecordingOptions) {
    const [isRecording, setIsRecording] = useState(false);
    const canvasStreamRef = useRef<MediaStream | null>(null);
    const stopRecordingRef = useRef<(() => void) | null>(null);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const compositeCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const animationRef = useRef<number | null>(null);

    const createCompositeCanvas = useCallback(() => {
        // Find static canvas
        const staticCanvas = document.querySelector('.excalidraw__canvas.static') as HTMLCanvasElement;

        if (!staticCanvas) {
            return null;
        }

        // Create composite canvas with same dimensions (off-screen for recording only)
        const compositeCanvas = document.createElement('canvas');
        compositeCanvas.width = staticCanvas.width;
        compositeCanvas.height = staticCanvas.height;

        // Don't add to DOM - keep it off-screen for recording only
        compositeCanvasRef.current = compositeCanvas;

        return compositeCanvas;
    }, []);

    const renderCompositeCanvas = useCallback(() => {
        const compositeCanvas = compositeCanvasRef.current;
        const staticCanvas = document.querySelector('.excalidraw__canvas.static') as HTMLCanvasElement;
        const newElementCanvas = document.querySelector('.excalidraw__canvas:not(.static):not(.interactive)') as HTMLCanvasElement;

        if (!compositeCanvas || !staticCanvas) return;

        // Update composite canvas size if excalidraw canvas size has changed
        if (compositeCanvas.width !== staticCanvas.width || compositeCanvas.height !== staticCanvas.height) {
            compositeCanvas.width = staticCanvas.width;
            compositeCanvas.height = staticCanvas.height;
        }

        const ctx = compositeCanvas.getContext('2d');
        if (!ctx) return;

        // Clear composite canvas
        ctx.clearRect(0, 0, compositeCanvas.width, compositeCanvas.height);

        // Draw static canvas (background layer)
        ctx.drawImage(staticCanvas, 0, 0);

        // Draw new element canvas on top (if it exists and has content)
        if (newElementCanvas) {
            ctx.drawImage(newElementCanvas, 0, 0);
        }
    }, []);

    const startRecording = useCallback(async () => {
        if (!room || !recordingEnabled) {
            return; // Don't error, just return if no room
        }

        try {
            // Create composite canvas
            const compositeCanvas = createCompositeCanvas();
            if (!compositeCanvas) {
                return false;
            }

            // Start continuous rendering of composite canvas
            const startCompositeRendering = () => {
                const animate = () => {
                    renderCompositeCanvas();
                    animationRef.current = requestAnimationFrame(animate);
                };
                animate();
            };

            // Start the composite rendering
            startCompositeRendering();

            // Create stream from composite canvas
            const stream = compositeCanvas.captureStream(frameRate);
            canvasStreamRef.current = stream;

            // Get the video track from the stream
            const videoTrack = stream.getVideoTracks()[0];
            if (!videoTrack) {
                onError(new Error('No video track available from composite canvas'));
                return false;
            }

            // Publish the track to Twilio
            const trackPublication = await room.localParticipant.publishTrack(videoTrack, {
                name: 'whiteboard',
                priority: 'low'
            });

            // Set up cleanup function
            stopRecordingRef.current = () => {
                // Stop the composite rendering
                if (animationRef.current) {
                    cancelAnimationFrame(animationRef.current);
                    animationRef.current = null;
                }

                // Clear composite canvas reference (not in DOM, so no need to remove)
                compositeCanvasRef.current = null;

                room.localParticipant.unpublishTrack(videoTrack);
                room.localParticipant.emit('trackUnpublished', trackPublication);
                videoTrack.stop();
                canvasStreamRef.current = null;
                setIsRecording(false);
            };

            // Handle track ending
            videoTrack.onended = stopRecordingRef.current;

            setIsRecording(true);
            return true; // Success
        } catch (error) {
            console.error('Failed to start whiteboard recording:', error);
            onError(error as Error);
            return false;
        }
    }, [room, onError, frameRate, createCompositeCanvas, renderCompositeCanvas]);

    const stopRecording = useCallback(() => {
        if (stopRecordingRef.current) {
            stopRecordingRef.current();
            stopRecordingRef.current = null;
        }
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    }, []);

    const tryStartRecording = useCallback(async () => {
        const success = await startRecording();
        if (!success && room && recordingEnabled && !isRecording) {
            // Retry after 500ms
            retryTimeoutRef.current = setTimeout(() => {
                tryStartRecording();
            }, 500);
        }
    }, [startRecording, room, recordingEnabled, isRecording]);

    // Automatically start/stop recording based on room and whiteboard state
    useEffect(() => {
        if (room && recordingEnabled && !isRecording) {
            // No delay - start immediately like the example
            tryStartRecording();
        } else if ((!room) && isRecording) {
            stopRecording();
        }

        return () => {
            if (!room || !recordingEnabled) {
                stopRecording();
            }
        }
    }, [room, recordingEnabled, isRecording, tryStartRecording, stopRecording]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopRecording();
            // Stop animation if still running
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
            // Clear composite canvas reference if still exists
            if (compositeCanvasRef.current) {
                compositeCanvasRef.current = null;
            }
        };
    }, [stopRecording]);

    return {
        isRecording
    } as const;
} 